/**
 * Pin or unpin one customer, inline in the change list.
 *
 * A pin is the manager's override of the distributor: "this customer stays with
 * this person, whatever the balance says." It is the single most consequential
 * thing this module writes, which is why the control lives next to the customer
 * it affects rather than in a separate screen — the decision is made while
 * reading a book, not while reading a form.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO is hide the failure. cm-api refuses a pin
 * whose customer is not in `crm.customer_settings`, by name and with a reason,
 * because a pin that matches no account survives every later check and is
 * silently ignored by assignment. That message is surfaced verbatim.
 */
import { useState } from 'react';
import { Button } from '@andavo/web-ui';
import { pinCustomer, StaffingWriteError, unpinCustomer } from '../mutations';

export interface PinControlProps {
  customerId: string;
  customerName: string;
  /** The `staffing_csm` row id of the CSM this row belongs to. */
  csmId: string | undefined;
  csmDisplayName: string;
  /** Already pinned to this CSM? Then the action is to remove it. */
  pinned: boolean;
  /** False when the reader lacks manage:staffing — the control explains rather than vanishing. */
  canManage: boolean;
  /** Re-read the book. Pins change assignment, so the whole model is stale after one. */
  onChanged: () => void;
}

export function PinControl({
  customerId,
  customerName,
  csmId,
  csmDisplayName,
  pinned,
  canManage,
  onChanged,
}: PinControlProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<StaffingWriteError | null>(null);

  // A CSM with no row id cannot be pinned to. That happens when the roster read
  // and the computed book disagree — worth saying rather than rendering a button
  // that would send an id the route cannot resolve.
  if (!canManage || csmId === undefined) return null;

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      if (pinned) await unpinCustomer(customerId);
      else await pinCustomer({ customerId, csmId });
      onChanged();
    } catch (err) {
      setError(
        err instanceof StaffingWriteError
          ? err
          : new StaffingWriteError('That did not work.', 0),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex flex-col gap-1">
      <Button
        aria-label={
          pinned
            ? `Unpin ${customerName} from ${csmDisplayName}`
            : `Pin ${customerName} to ${csmDisplayName}`
        }
        disabled={busy}
        onClick={run}
        size="sm"
        variant={pinned ? 'secondary' : 'ghost'}
      >
        {busy ? '…' : pinned ? 'Unpin' : 'Pin'}
      </Button>
      {error ? (
        <span className="text-content-danger text-xs" role="alert">
          {error.message}
          {error.remedy ? ` — ${error.remedy}` : null}
        </span>
      ) : null}
    </span>
  );
}
